// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { createContext, useState, FC, useEffect } from "react";
// @ts-ignore
import { useAuthContext } from "@asgardeo/auth-react";
import { useIdleTimer } from "react-idle-timer";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../slices/store";
import Loading from "../pages/Loading";
import {
  setUserAuthData,
  setIsAuthenticated,
  setLoading,
} from "../slices/authSlice/authSlice";
import { getContact } from "../slices/contactSlice/contact";
import { APIService } from "../utils/apiService";
import { getAppData } from "../slices/appSlice/app";
import { routes } from "../config/constant";

interface Props {
  children: React.ReactNode;
}

interface AuthContextType {
  appSignIn: () => void;
  appSignOut: () => void;
}

// Idle time popup appear after 20 minutes
const timeout = 1200_000;
const promptBeforeIdle = 4_000;

export const AuthContext = createContext({} as AuthContextType);

const AppAuthProvider: FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state: RootState) => state.auth);
  const appMaintenanceStatus = useAppSelector((state: RootState) => state.app);
  const [appState, setAppState] = useState<"logout" | "active" | "loading">(
    "loading"
  );
  const [open, setOpen] = useState<boolean>(false);
  const onPrompt = () => {
    if (appState === "active") setOpen(true);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });
  
  const {
    signIn,
    signOut,
    getIDToken,
    getDecodedIDToken,
    refreshAccessToken,
    getBasicUserInfo,
    isAuthenticated,
    state,
  } = useAuthContext();

  const isPublicRoute = React.useMemo(() => {
    const path = typeof window !== 'undefined' ? (window.location?.pathname || "") : "";
    return (
      path.startsWith(routes.certificateBase) ||
      path.startsWith(routes.certificatesBase)
    );
  }, []);

  useEffect(() => {
    // Function to handle localStorage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "cp-app-status") {
        const newValue = event.newValue;
        if (newValue === "active") setAppState("active");
        else setAppState("logout");
      }
    };
  
    // Add event listener for 'storage' event
    window.addEventListener('storage', handleStorageChange);
  
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  useEffect(() => {
    if (localStorage.getItem("cp-app-status") !== "active") {
      setAppState("logout");
    }
  }, []);

  useEffect(() => {
    if (state.isLoading) {
      return;
    }
    if (state.isAuthenticated) {
      (async () => {
        try {
          const [userInfo, idToken, decodedIdToken] = await Promise.all([
            getBasicUserInfo(),
            getIDToken(),
            getDecodedIDToken(),
          ]);
          dispatch(setLoading(true));
          dispatch(setUserAuthData({ userInfo, idToken, decodedIdToken }));
          new APIService(idToken, refreshToken);

          const appDataResult = await dispatch(getAppData());
          const payload = appDataResult.payload as { status?: { isAppInMaintenanceState?: boolean } } | undefined;
          const isMaintenanceMode = payload?.status?.isAppInMaintenanceState;

          if (userInfo.email && isMaintenanceMode === false) {
            await dispatch(getContact(userInfo.email));
          }
          dispatch(setIsAuthenticated(true));
          if (appDataResult.meta.requestStatus === "fulfilled") {
            setAppState("active");
          } else if (appDataResult.meta.requestStatus === "rejected") {
            setAppState("logout");
          } else {
            setAppState("loading");
          }
          localStorage.setItem("cp-app-status", "active");
        } catch (error) {
          console.error("Error during authentication:", error);
          setAppState("logout");
        } finally {
          dispatch(setLoading(false));
        }
      })();
    } else {
      const status = localStorage.getItem("cp-app-status");
      if (status === "active") {
        // Avoid forcing sign-in on public certificate routes so shared links work for logged-in sessions
        if (!isPublicRoute) {
          appSignIn(); 
        } else {
          setAppState("logout");
        }
      } else {
        setAppState("logout");
        localStorage.setItem("cp-app-status", "inactive");
      }
    }
  }, [
    state.isAuthenticated,
    state.isLoading,
    isPublicRoute,
  ]);

  // const appSignIn = async () => {
  //   setAppState("loading");
  //   await signIn();
  //   setAppState("loading");
  // };

  // useEffect(() => {
  //   if (appState === "active") {
  //     if (state.isAuthenticated) {
  //       if (appStats.state === "loading") {
  //         setAppState("loading");
  //       } else {
  //         dispatch(getStatus());
  //       }
  //     } else {
  //       signIn();
  //     }
  //   } else if (appState === "loading" && appStats.state !== "loading") {
  //     setAppState("active");
  //   }
  // }, [appStats.state, state.isAuthenticated, appState]);

  const appSignIn = async () => {
    if (!state.isLoading) {
      setAppState("loading");
      await signIn();
    }
  };

  const appSignOut = async () => {
    // setAppState("loading");
    setAppState("logout");
    localStorage.setItem("cp-app-status", "inactive");
    // await revokeAccessToken();
    await signOut();
    // localStorage.removeItem("cp-app-token");
    // dispatch(setIsAuthenticated(false));
    // setIsLoggedIn(false);
    // setAppState("logout");
  };

  // const revokeToken = () => {
  //   revokeAccessToken();
  // };

  // const handleIDToken = async () => {
  //   return new Promise<{ idToken: string }>(async (resolve) => {
  //     let idToken = await getIDToken();
  //     localStorage.setItem("cp-app-token", idToken);
  //     resolve({ idToken: idToken });
  //   });
  // };

  const refreshToken = () => {
    return new Promise<{ idToken: string }>(async (resolve) => {
      const isUserAuthenticated = await isAuthenticated();
      if (isUserAuthenticated) {
        resolve({idToken: await getIDToken()});
      } else {
        refreshAccessToken()
          .then(async () => {
            resolve({idToken: await getIDToken()});
          })
          .catch(() => {
            appSignOut();
          });
      }
    });
  };

  return (
    <>
      {appState === "loading" || state.isLoading || auth.loading ? (
        <Loading message={"WSO2 Certification Portal"} />
      ) : (
        <>
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Are you still there?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                It looks like you've been inactive for a while. Would you like
                to continue?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Continue</Button>
              <Button onClick={() => appSignOut()}>Logout</Button>
            </DialogActions>
          </Dialog>
          <AuthContext.Provider
            value={{
              appSignIn,
              appSignOut
            }}
          >
            {children}
          </AuthContext.Provider>
        </>
      )}
    </>
  );
};

export default AppAuthProvider;
